import { CreateInDialogButtonProps } from '@react-admin/ra-form-layout';
import { useCallback } from 'react';
import { RaRecord, required, SelectInput, TextInput } from 'react-admin';

import { PostBoInvestorProfileEndpoint } from '@Common/types/bo/b2b/investor_profile';

import { investorProfileProductCategoryChoices } from '@sh4res/bo-common/choices/investorProfile';
import { CreateInDialogButton, ShowSection } from '@sh4res/bo-common/components';
import { SimpleForm } from '@sh4res/bo-common/components/SimpleForm';
import { useResourceBasePath } from '@sh4res/bo-common/hooks';
import { transformFormGridToBoGrid } from '@sh4res/bo-common/resources/investor_profile/components/B2bInvestorProfileGrid/helpers/transformFormGridToBoGrid';
import { transformFormSicavGridToBoGrid } from '@sh4res/bo-common/resources/investor_profile/components/B2bInvestorProfileGrid/helpers/transformFormSicavGridToBoGrid';
import { B2bInvestorProfileGridWrapper } from '@sh4res/bo-common/resources/investor_profile/components/B2bInvestorProfileGridWrapper/B2bInvestorProfileGridWrapper';
import { B2bInvestorProfileRiskProfileInput } from '@sh4res/bo-common/resources/investor_profile/components/B2bInvestorProfileRiskProfileInput/B2bInvestorProfileRiskProfileInput';
import { getB2BInvestorProfileResourceReference } from '@sh4res/bo-common/resources/investor_profile/helpers/getB2BInvestorProfileResourceReference';

export const B2bInvestorProfileCreate = ({
  companyId,
  contractId,
  testId,
  ...buttonProps
}: CreateInDialogButtonProps['ButtonProps'] & {
  companyId?: number;
  contractId?: number;
  testId: string;
}) => {
  const resourceBasePath = useResourceBasePath();
  const handleTransform = useCallback(
    (data: RaRecord): PostBoInvestorProfileEndpoint['Client']['RequestBody'] => ({
      b2b_company_id: companyId ?? 0,
      b2b_contract_id: contractId ?? null,
      template_name: data.grid_name,
      b2b_plan_category: data.product_category,
      risk_profile: data.grid_template,
      allocations:
        data.grid_template === 'sicav'
          ? transformFormSicavGridToBoGrid(data.sicav_grid)
          : transformFormGridToBoGrid(data.grid),
    }),
    [companyId, contractId],
  );

  return (
    <CreateInDialogButton
      testId={`${testId}_OpenButton`}
      resource={getB2BInvestorProfileResourceReference(resourceBasePath)}
      record={{ b2b_company_id: companyId, b2b_contract_id: contractId }}
      fullWidth
      maxWidth="lg"
      title="resources.bo_common.b2b.investor_profile.create.title"
      ButtonProps={buttonProps}
      transform={handleTransform}
      PaperProps={{
        sx: {
          borderRadius: '12px',
        },
      }}
      sx={{
        '#edit-dialog-title': {
          fontWeight: 700,
        },
      }}>
      <SimpleForm>
        <ShowSection
          sx={{ width: '100%' }}
          noCard={true}
          label="resources.bo_common.b2b.investor_profile.create.product_category">
          <SelectInput
            sx={{ width: '100%' }}
            source="product_category"
            label="resources.bo_common.b2b.investor_profile.create.product_category"
            validate={[required()]}
            isRequired
            choices={investorProfileProductCategoryChoices}
            data-testid={`${testId}_ProductCategoryInput`}
          />
        </ShowSection>
        <ShowSection
          sx={{ width: '100%' }}
          noCard={true}
          label="resources.bo_common.b2b.investor_profile.create.name">
          <TextInput
            sx={{ width: '100%' }}
            label="resources.bo_common.b2b.investor_profile.create.name"
            source="grid_name"
            validate={[required()]}
            isRequired
            data-testid={`${testId}_NameInput`}
          />
        </ShowSection>
        <B2bInvestorProfileRiskProfileInput testId={`${testId}_RiskProfileInput`} />
        <ShowSection
          sx={{ width: '100%' }}
          noCard={true}
          label="resources.bo_common.b2b.investor_profile.create.grid_configuration.title"
          subtitle="resources.bo_common.b2b.investor_profile.create.grid_configuration.subtitle">
          <B2bInvestorProfileGridWrapper />
        </ShowSection>
      </SimpleForm>
    </CreateInDialogButton>
  );
};
